import { abortConversation, createConversation } from '@api/agent';
import { useAuthContext } from '@authentication/Authentication';
import { useLocation } from '@gatsbyjs/reach-router';
import { agentPageContextAtom, agentPanelOpenAtom, type PageContext } from '@hooks/agentPanelAtoms';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useWorkspaceContext } from '@navigation/WorkspaceContext';
import { useAtomValue, useSetAtom } from 'jotai';
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgentChatConversationList } from './AgentChatConversationList';
import { AgentChatView } from './AgentChatView';

/**
 * Extract the current page section and (optionally) the entity being viewed
 * from the URL pathname. Mirrors the SideNav tab-detection logic so the
 * agent's context matches what the user sees highlighted in the sidebar.
 */
function extractPageDetails(
  pathname: string,
  workspaceId: string | undefined,
  serviceOnly: boolean,
): Pick<PageContext, 'section' | 'entityId' | 'entityType'> {
  // Workspace-scoped sections (when a workspace is selected and org is not serviceOnly)
  if (workspaceId && pathname.includes('workspaces') && !serviceOnly) {
    // Order matters: check sub-sections before the workspace-home fallback
    const wsSections = [
      { match: 'volumes', section: 'workspace-volumes', entity: 'volume' },
      { match: 'graphs', section: 'workspace-graphs', entity: 'graph' },
      { match: 'jobs', section: 'workspace-jobs', entity: 'job' },
      { match: 'datasets', section: 'workspace-datasets', entity: 'dataset' },
      { match: 'experiments', section: 'workspace-experiments', entity: 'experiment' },
    ] as const;

    const matchingSection = wsSections.find(({ match }) => pathname.includes(match));
    if (matchingSection) {
      const entityId = extractEntityId(pathname, matchingSection.match);
      return {
        section: matchingSection.section,
        entityId,
        entityType: entityId ? matchingSection.entity : undefined,
      };
    }
    return { section: 'workspace-home' };
  }

  // Profile checked first (matches SideNav line 24)
  if (pathname.includes('profile')) {
    return { section: 'profile' };
  }

  // Organisation-level sections — order mirrors SideNav (lines 51-93)
  // 'examples' first because its sub-routes include other section names
  const orgSections = [
    { match: 'examples', section: 'examples' },
    { match: 'sample-gallery', section: 'sample-gallery' },
    { match: 'workspaces', section: 'workspaces' },
    { match: 'remote-sessions', section: 'remote-sessions' },
    { match: 'channels', section: 'channels', entity: 'channel' },
    { match: '/services', section: 'services', entity: 'service' },
    { match: 'volumes', section: 'volumes', entity: 'volume' },
    { match: 'rules', section: 'rules' },
    { match: 'gan-models', section: 'gan-models' },
    { match: 'annotation-maps', section: 'annotation-maps' },
    { match: 'settings', section: 'settings' },
    { match: 'mcp-configs', section: 'mcp-configs' },
    { match: 'skills', section: 'skills' },
    { match: 'servers', section: 'servers' },
    { match: 'agent', section: 'agent' },
  ] as const;

  const matchingSection = orgSections.find(({ match }) => pathname.includes(match));
  if (matchingSection) {
    const entityKey = 'entity' in matchingSection ? matchingSection.entity : undefined;
    const entityId = entityKey ? extractEntityId(pathname, matchingSection.match) : undefined;
    return {
      section: matchingSection.section,
      entityId,
      entityType: entityId ? entityKey : undefined,
    };
  }
  return { section: 'home' };
}

/**
 * Given a pathname and a section keyword, extract the UUID that follows
 * the keyword segment. e.g. "/org/workspaces/ws/datasets/abc-123/logs" → "abc-123"
 *
 * Uses positive regex matching rather than a blocklist to distinguish entity IDs
 * from path keywords. This avoids false positives when new route keywords are added.
 */
function extractEntityId(pathname: string, sectionKeyword: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  const idx = segments.findIndex(
    (s) => s === sectionKeyword || s === sectionKeyword.replace('/', ''),
  );
  if (idx >= 0 && idx + 1 < segments.length) {
    const candidate = segments[idx + 1];
    // Match UUIDs (8-4-4-4-12 hex) or shorter hex-like IDs (8+ hex chars).
    // This positive match is safer than a blocklist of path keywords.
    if (
      candidate &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate)
    ) {
      return candidate;
    }
    // Fallback: accept any segment that looks like a hex/alphanumeric ID (8+ chars, contains a digit)
    if (
      candidate &&
      candidate.length >= 8 &&
      /[0-9]/.test(candidate) &&
      /^[0-9a-zA-Z_-]+$/.test(candidate)
    ) {
      return candidate;
    }
  }
  return undefined;
}

/**
 * Top-level panel container for the agent chat.
 * Always mounted in PageLayout's grid — visibility controlled by
 * the grid column width transitioning between 0px and 400px.
 *
 * Responsibilities:
 * - View switching between conversation list and active chat
 * - Page context sync (org, workspace, path → agentPageContextAtom)
 * - Organization change handling (clear state, abort streams)
 */
export const AgentChatPanel = () => {
  const { token } = useAuthContext();
  const { organization } = useOrganizationContext();
  const { workspaceId, workspace } = useWorkspaceContext();
  const location = useLocation();
  const panelOpen = useAtomValue(agentPanelOpenAtom);
  const setPageContext = useSetAtom(agentPageContextAtom);

  const organizationId = organization?.organizationId ?? '';

  // Active conversation state — null means show the conversation list
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeConversationTitle, setActiveConversationTitle] = useState<string | undefined>();

  // Track previous org to detect org switches
  const prevOrgIdRef = useRef(organizationId);

  // Sync page context atom whenever navigation context changes
  useEffect(() => {
    const details = extractPageDetails(
      location?.pathname ?? '',
      workspaceId,
      organization?.serviceOnly ?? false,
    );

    setPageContext({
      organizationId: organizationId || undefined,
      organizationName: organization?.name,
      workspaceId: workspaceId || undefined,
      workspaceName: workspace?.name,
      path: location?.pathname,
      ...details,
    });
  }, [
    organizationId,
    organization?.name,
    organization?.serviceOnly,
    workspaceId,
    workspace?.name,
    location?.pathname,
    setPageContext,
  ]);

  // Handle organization switch — clear state and abort any active stream
  useEffect(() => {
    if (prevOrgIdRef.current && prevOrgIdRef.current !== organizationId) {
      if (activeConversationId && token) {
        abortConversation(token, activeConversationId).catch(() => {});
      }
      setActiveConversationId(null);
      setActiveConversationTitle(undefined);
    }
    prevOrgIdRef.current = organizationId;
  }, [organizationId, activeConversationId, token]);

  // Abort streams and reset to list view when panel closes
  useEffect(() => {
    if (!panelOpen && activeConversationId && token) {
      abortConversation(token, activeConversationId).catch(() => {});
    }
    if (!panelOpen) {
      setActiveConversationId(null);
      setActiveConversationTitle(undefined);
    }
  }, [panelOpen, activeConversationId, token]);

  /** Handle selecting a conversation from the list */
  const handleSelectConversation = useCallback((conversationId: string, title?: string) => {
    setActiveConversationId(conversationId);
    setActiveConversationTitle(title);
  }, []);

  /** Handle navigating back to the conversation list */
  const handleBack = useCallback(() => {
    setActiveConversationId(null);
    setActiveConversationTitle(undefined);
  }, []);

  /** Create a new conversation and switch to it directly from the chat view */
  const handleNewChat = useCallback(async () => {
    if (!token || !organizationId) return;
    try {
      const convo = await createConversation(token, { organizationId, workspaceId });
      posthog.capture('agent-chat:user_conversation_created', {
        organizationId,
        workspaceId,
        conversationId: convo.conversationId,
        source: 'chat-view',
      });
      setActiveConversationId(convo.conversationId);
      setActiveConversationTitle(undefined);
    } catch (err) {
      console.error('[AgentChatPanel] Failed to create conversation:', err);
    }
  }, [token, organizationId, workspaceId]);

  // Don't render content if not authenticated
  if (!token || !organizationId) return null;

  return (
    <Stack
      sx={{
        minWidth: 400,
        height: '100%',
        borderLeft: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paperCard',
        overflow: 'hidden',
      }}
      data-testid="agent-chat-panel"
    >
      {activeConversationId ? (
        <AgentChatView
          token={token}
          conversationId={activeConversationId}
          conversationTitle={activeConversationTitle}
          onBack={handleBack}
          onNewChat={() => void handleNewChat()}
        />
      ) : (
        <AgentChatConversationList
          token={token}
          organizationId={organizationId}
          workspaceId={workspaceId}
          onSelectConversation={handleSelectConversation}
        />
      )}
    </Stack>
  );
};
